// ============================================================================
// Section 4 — Role switcher (left tabs + right image + bullets per tab)
// ============================================================================

import { useState } from "react";
import { Home, Users, UserCheck, Building2 } from "lucide-react";

const ROLES = [
  {
    id: "owners",
    label: "STR Hosts (Owners)",
    icon: Home,
    bullets: [
      "See schedule, tasks, quality, inventory, and issues across properties.",
      "Manage reservations and turnover timelines in one place.",
      "Control access and team assignments per property.",
    ],
    imageAlt: "Owner schedule timeline",
    // Swap in real screenshot: /screenshots/owner-schedule.png or similar
    imagePlaceholder: "owner",
  },
  {
    id: "team-admins",
    label: "Team Admins (Managers/Admins)",
    icon: Users,
    bullets: [
      "Add workers, assign tasks, and manage turnovers inside the properties you're assigned.",
      "Invite cleaners and delegate from a single dashboard.",
      "Keep turnovers consistent across your team.",
    ],
    imageAlt: "Team admin dashboard",
    imagePlaceholder: "team",
  },
  {
    id: "cleaners",
    label: "Cleaners (Workers/Viewers)",
    icon: UserCheck,
    bullets: [
      "See assigned tasks only. Start, complete, upload photos, report issues.",
      "Mobile-first task list with checklist steps and timers.",
      "Flag maintenance and low inventory as you go.",
    ],
    imageAlt: "Cleaner task view",
    imagePlaceholder: "cleaner",
  },
  {
    id: "companies",
    label: "Cleaning Companies",
    icon: Building2,
    bullets: [
      "Organize staff across multiple properties and keep turnover execution consistent.",
      "One dashboard for schedules, quality, and team performance.",
      "Scale delegation without losing visibility.",
    ],
    imageAlt: "Cleaning company view",
    imagePlaceholder: "company",
  },
];

function RoleImagePlaceholder({ type }: { type: string }) {
  const gradients: Record<string, string> = {
    owner: "from-primary/20 via-primary/10 to-background",
    team: "from-blue-500/20 via-blue-400/10 to-background",
    cleaner: "from-amber-500/20 via-amber-400/10 to-background",
    company: "from-emerald-500/20 via-emerald-400/10 to-background",
  };
  return (
    <div
      className={`absolute inset-0 flex h-full w-full items-center justify-center bg-gradient-to-b ${gradients[type] ?? "from-muted to-background"}`}
      aria-hidden
    >
      <span className="text-sm font-medium text-muted-foreground">Screenshot — {type}</span>
    </div>
  );
}

export default function RoleSwitcherSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = ROLES[activeIndex];

  return (
    <section id="roles" className="flex min-h-[85vh] flex-col justify-center border-t border-border/60 bg-muted/30 py-12 sm:py-16">
      <div className="mx-auto w-full max-w-6xl flex-1 px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12 lg:items-center">
          {/* Left: eyebrow + headline + paragraph + vertical tabs */}
          <div className="min-w-0">
            <p className="text-sm font-medium uppercase tracking-wider text-primary">Built for every role</p>
            <h2 className="mt-2 text-3xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl">
              Everyone sees what they need — nothing else.
            </h2>
            <p className="mt-4 max-w-md text-muted-foreground sm:text-lg">
              Owners manage properties. Team admins manage people. Workers execute tasks. Stravo keeps permissions
              clean so delegation scales.
            </p>

            {/* Vertical tab list */}
            <nav className="mt-10" aria-label="Roles">
              <ul className="flex flex-col gap-0">
                {ROLES.map((role, index) => {
                  const isActive = index === activeIndex;
                  const Icon = role.icon;
                  return (
                    <li key={role.id}>
                      <button
                        type="button"
                        onMouseEnter={() => setActiveIndex(index)}
                        onFocus={() => setActiveIndex(index)}
                        className={`flex w-full items-center gap-3 border-l-2 py-4 pl-4 text-left transition-colors ${
                          isActive
                            ? "border-primary bg-primary/5 text-foreground"
                            : "border-transparent text-muted-foreground hover:border-muted-foreground/30 hover:bg-muted/50 hover:text-foreground"
                        }`}
                        aria-current={isActive ? "true" : undefined}
                      >
                        <span
                          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                            isActive ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="font-medium">{role.label}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>

          {/* Right: image fits viewport, no scroll; hover role row to switch */}
          <div className="flex min-h-0 flex-col gap-4" style={{ maxHeight: "min(72vh, 640px)" }}>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[32px] border border-border/80 bg-card shadow-sm">
              <div
                className="relative w-full flex-1 min-h-[240px] overflow-hidden"
                style={{ maxHeight: "min(52vh, 420px)" }}
              >
                <RoleImagePlaceholder type={active.imagePlaceholder} />
              </div>
            </div>
            <ul className="flex flex-col gap-1.5 shrink-0" aria-label={`Highlights for ${active.label}`}>
              {active.bullets.map((bullet, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
